import * as React from 'react';
import { Checkbox, DeletableInput, Input } from '../Input';
import { Button } from '../Button';
import { Validated } from '../Validated';
import {
  FieldType,
  IDrawingObject,
  getFieldRowClassName,
  getReferencedFieldRemoveTitle,
} from './DrawingObjectsInput';
import { TableScroll } from '../TableScroll';

export enum ValueType {
  Int = 1,
  Float,
  String,
  Option,
}

export interface IAdditionalField {
  readonly id?: number;
  name: string;
  unit: string;
  valueType: ValueType;
  options: string[];
  export: boolean;
}

function defaultAdditionalField(): IAdditionalField {
  return {
    name: '',
    valueType: ValueType.Int,
    export: false,
    options: [],
    unit: '',
  };
}

export interface AdditionalFieldsInputProps {
  value: IAdditionalField[];
  drawingObjects: IDrawingObject[];
  validation: string | undefined;
  /** 入力チェックで誤りが見つかった行の位置。該当する行を強調する */
  invalidRowIndexes: number[];
  onChange: (fields: IAdditionalField[]) => void;
  onDrawingObjectsChange: (objs: IDrawingObject[]) => void;
}

export const AdditoinalFieldsInput: React.FC<AdditionalFieldsInputProps> = function (
  props
) {
  function changeValue(index: number, field: IAdditionalField) {
    const next = props.value.slice();
    next[index] = field;
    props.onChange(next);
  }
  function addValue() {
    props.onChange([...props.value, defaultAdditionalField()]);
  }
  /** index番目の手入力項目が、作画文字列のどれかに参照されているか */
  function isReferenced(index: number): boolean {
    return props.drawingObjects.some((obj) => {
      if (obj.fieldType !== FieldType.AdditionalField) {
        return false;
      }

      return obj.fieldIndex === index;
    });
  }
  function removeValue(index: number) {
    if (isReferenced(index)) {
      return;
    }

    // 削除した行より後ろを参照している作画文字列の参照位置を1つ詰める
    const shiftedDrawingObjects = props.drawingObjects.map((obj) => {
      if (obj.fieldType !== FieldType.AdditionalField) {
        return obj;
      }

      if (obj.fieldIndex <= index) {
        return obj;
      }

      return { ...obj, fieldIndex: obj.fieldIndex - 1 };
    });

    props.onDrawingObjectsChange(shiftedDrawingObjects);
    props.onChange(props.value.filter((_, i) => i !== index));
  }

  function row(field: IAdditionalField, index: number) {
    const referenced = isReferenced(index);

    return (
      <tr
        key={index}
        className={getFieldRowClassName(props.invalidRowIndexes.indexOf(index) !== -1)}
      >
        <td>
          <Input
            value={field.name}
            onChange={(event) =>
              changeValue(index, { ...field, name: event.target.value })
            }
          />
        </td>
        <td>
          <select
            value={field.valueType}
            onChange={(event) => {
              let valueType: ValueType;
              switch (event.target.value) {
                case '1':
                  valueType = ValueType.Int;
                  break;
                case '2':
                  valueType = ValueType.Float;
                  break;
                case '4':
                  valueType = ValueType.Option;
                  break;
                case '3':
                default:
                  valueType = ValueType.String;
                  break;
              }

              changeValue(index, { ...field, valueType });
            }}
          >
            <option value="1">整数</option>
            <option value="2">小数</option>
            <option value="3">文字列</option>
            <option value="4">選択式</option>
          </select>
        </td>
        <td>
          {(function () {
            if (field.valueType !== ValueType.Option) {
              return;
            }

            return (
              <ul className="PlaceCreateCodePage-additional_field_options_list">
                {field.options.map((option, optionIndex) => (
                  <li key={optionIndex}>
                    <DeletableInput
                      value={option}
                      onChange={(event) => {
                        const v = event.target.value;
                        const options = field.options.slice();
                        options[optionIndex] = v;

                        changeValue(index, { ...field, options });
                      }}
                      onDelete={() => {
                        const options = field.options.filter(
                          (_, i) => i !== optionIndex
                        );
                        changeValue(index, { ...field, options });
                      }}
                    />
                  </li>
                ))}
                <li>
                  <Button
                    type="button"
                    mini={true}
                    color="white"
                    onClick={() => {
                      const options = [...field.options, ''];
                      changeValue(index, { ...field, options });
                    }}
                  >
                    選択肢を追加
                  </Button>
                </li>
              </ul>
            );
          })()}
        </td>
        <td>
          <Checkbox
            value={field.export}
            onChange={(value) =>
              changeValue(index, { ...field, export: value })
            }
          />
        </td>
        <td>
          <Input
            value={field.unit}
            onChange={(event) =>
              changeValue(index, { ...field, unit: event.target.value })
            }
          />
        </td>
        <td className="App-table_button_cell">
          <Button
            type="button"
            mini={true}
            color="red"
            disabled={referenced}
            title={getReferencedFieldRemoveTitle(referenced)}
            onClick={() => removeValue(index)}
          >
            削除
          </Button>
        </td>
      </tr>
    );
  }

  return (
    <div>
      <Validated message={props.validation}>
        <TableScroll storageKey="codeAdditionalFields">
          <table className="App-table">
            <thead>
              <tr>
                <th>
                  名称
                  <span className="App-label_required" aria-hidden="true">*</span>
                </th>
                <th>値の種類</th>
                <th>選択肢</th>
                <th>集計表に出力</th>
                <th>単位</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>{props.value.map(row)}</tbody>
          </table>
        </TableScroll>
      </Validated>
      <div className="PlaceCreateCodePage-table_add_row">
        <Button type="button" mini={true} color="white" onClick={() => addValue()}>
          行を追加
        </Button>
      </div>
    </div>
  );
};
